import { RefObject, useEffect } from 'react';
import { canMoveFurther, isScrollArea, overflows, wheelPixels } from '../Common/OpenCard/scrollPane';

/**
 * The biggest area in the content that scrolls — the rows of a list in a card
 * that stays put — or the content itself where that is what scrolls. Never the
 * body of an open row: that is scrolled from inside it.
 */
const findScroller = (content: HTMLElement): HTMLElement | null => {
    if (overflows(content)) return content;
    let best: HTMLElement | null = null;
    let bestArea = 0;
    content.querySelectorAll<HTMLElement>('*').forEach((el) => {
        // size first: reading the style of every element is what costs
        if (el.scrollHeight <= el.clientHeight + 1) return;
        const area = el.clientWidth * el.clientHeight;
        if (area > bestArea && isScrollArea(el) && !el.closest('[data-open-row]')) {
            best = el;
            bestArea = area;
        }
    });
    return best;
};

/**
 * A wheel turned over the nav, the top bar or the footer scrolls the page, as
 * it did when the window was what scrolled — now that the content scrolls in
 * a pane of its own, or inside a card in it, nothing else would.
 *
 * Also while a row is open: a wheel over it scrolls only its body (see
 * useAlignOnOpen), but one from out here moves the rows, the open one with
 * them. Not where what is under the cursor can scroll that way itself: a long
 * nav still scrolls the nav.
 */
const useWheelToContent = (rootRef: RefObject<HTMLElement>, contentSelector: string) => {
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;
        // the last area found, kept while it is still there and still scrolls —
        // wheel events come thick and fast, and the search is not free
        let found: HTMLElement | null = null;

        const onWheel = (event: WheelEvent) => {
            // a pinch is a zoom; a sideways swipe is not the page's
            if (event.ctrlKey || !event.deltaY || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
            const content = root.querySelector<HTMLElement>(contentSelector);
            const target = event.target as Element | null;
            if (!content || !target || content.contains(target)) return;

            const down = event.deltaY > 0;
            for (let el: Element | null = target; el && el !== root; el = el.parentElement) {
                if (overflows(el) && canMoveFurther(el, down)) return;
            }

            if (!found || !content.contains(found) || !overflows(found)) found = findScroller(content);
            if (!found) return;
            found.scrollTop += wheelPixels(event, found);
        };

        root.addEventListener('wheel', onWheel, { passive: true });
        return () => root.removeEventListener('wheel', onWheel);
    }, [rootRef, contentSelector]);
};

export default useWheelToContent;
